import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import SearchBar from '@/features/documents/components/search-bar';
import { useCanManageOrgMembers } from '@/features/org/hooks/use-can-manage-org-members';
import { ProjectHubFilter } from '@/features/projects/projects-hub/components/project-hub-filter/project-hub-filter';
import { ProjectStatusIndicator } from '@/features/projects/projects-hub/components/project-status-indicator';
import { useProjects } from '@/features/projects/shared/hooks/use-projects';
import { ProjectStatus } from '@/features/projects/shared/types/project-status';
import PlusIcon from '@/shared/assets/plus.svg?react';
import { DefaultLayout } from '@/shared/components/layouts/default-layout';
import { LoadingIndicator } from '@/shared/components/loading-indicator';
import { PaginationBar } from '@/shared/components/pagination-bar';
import { EmptyTablePlaceholder } from '@/shared/components/table/empty-table-placeholder';
import { CustomTable } from '@/shared/components/table/table';
import { CustomTableHeader } from '@/shared/components/table/table-header';
import { CustomTableRow } from '@/shared/components/table/table-row';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { TableBody } from '@/shared/components/ui/table';
import {
  useHasProjectHubFilters,
  useProjectHubFilterStore,
} from '@/shared/hooks/use-hub-filter-store';
import { usePagination } from '@/shared/hooks/use-pagination';
import { ProjectWithContractors } from '@/shared/types/api';

const PROJECT_TABLE_COLUMN_WIDTHS = [
  'w-[264px]',
  'w-[204px]',
  'w-[242px]',
  'w-[256px]',
  'w-[256px]',
  'flex-1',
];

function getProjectTableColumns(t: (...args: any[]) => string): string[] {
  return [
    t('table_project'),
    t('table_status'),
    t('table_partner'),
    t('table_last_entry'),
    t('table_role'),
    t('table_notifications'),
  ];
}

export const ProjectsHubPage = () => {
  const { t } = useTranslation('project', { keyPrefix: 'ProjectsHub' });
  const { t: sharedT } = useTranslation('project', { keyPrefix: 'Shared' });

  const navigate = useNavigate();
  const { isFetching, isPending, data: projects, isError } = useProjects();
  const canManage = useCanManageOrgMembers();

  const hasFilters = useHasProjectHubFilters();
  const { searchQuery, setSearchQuery } = useProjectHubFilterStore();

  const tableColumns = getProjectTableColumns(t);

  return (
    <DefaultLayout
      title={t('title')}
      headerActions={[
        <SearchBar
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
          placeholder={t('search_placeholder')}
        />,
        canManage && (
          <Button
            variant={'greenGradient'}
            btnType="secondary"
            onClick={() => navigate(paths.projects.create.base.path)}
          >
            <PlusIcon className="size-4 sm:size-4" />
            <span className="hidden sm:inline">
              {t('create_project_button_label')}
            </span>
          </Button>
        ),
        <ProjectHubFilter />,
      ]}
    >
      {isFetching || isPending ? (
        <LoadingIndicator className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
      ) : !projects || isError ? (
        <span>{sharedT('could_not_fetch_projects')}</span>
      ) : projects.length === 0 ? (
        <EmptyTablePlaceholder
          title={
            hasFilters ? t('no_projects_found') : t('create_project_title')
          }
          description={hasFilters ? undefined : t('create_project_description')}
          columns={tableColumns}
          action={
            hasFilters || !canManage
              ? undefined
              : () => navigate(paths.projects.create.base.path)
          }
          dottedBorder
        />
      ) : (
        <PaginatedProjectsBody projects={projects} />
      )}
    </DefaultLayout>
  );
};

const PAGE_SIZE = 10;

const PaginatedProjectsBody = ({
  projects,
}: {
  projects: ProjectWithContractors[];
}) => {
  const { paginatedData, currentPage, totalPages, setCurrentPage } =
    usePagination({ data: projects, pageSize: PAGE_SIZE });

  return (
    <>
      <ProjectsBody projects={paginatedData} />
      <PaginationBar
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </>
  );
};

type ProjectsBodyProps = { projects: ProjectWithContractors[] };

const ProjectsBody = ({ projects }: ProjectsBodyProps) => {
  const { t } = useTranslation('project', { keyPrefix: 'ProjectsHub' });
  const { t: sharedT } = useTranslation('shared');
  const navigate = useNavigate();

  const columns = getProjectTableColumns(t);
  const columnWidths = PROJECT_TABLE_COLUMN_WIDTHS;

  return (
    <CustomTable>
      <CustomTableHeader columns={columns} columnWidths={columnWidths} />
      <TableBody>
        {projects.map((project, index) => (
          <CustomTableRow
            key={project.id}
            onClick={() =>
              navigate(
                paths.projects.details.base.getHref(project.id.toString()),
              )
            }
            props={[
              {
                value: {
                  type: 'string',
                  text:
                    project.type === 'project'
                      ? project.name
                      : (project.name ?? ''),
                },
                isLeftMost: true,
                width: columnWidths[0] || 'flex-1',
              },
              {
                value: {
                  type: 'custom',
                  item: (
                    <ProjectStatusIndicator
                      status={project.status as ProjectStatus}
                    />
                  ),
                },
                width: columnWidths[1] || 'flex-1',
              },
              {
                value: {
                  type: 'custom',
                  item:
                    project.contractors.length > 0 ? (
                      <div className="flex flex-row gap-1">
                        {project.contractors.slice(0, 2).map((partner) => (
                          <Badge
                            className="line-clamp-1"
                            variant="table"
                            key={partner.id}
                          >
                            {partner.alias}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-custom-grey">—</span>
                    ),
                },
                width: columnWidths[2] || 'flex-1',
              },
              project.last_entry_date
                ? {
                    value: {
                      type: 'date',
                      date: new Date(project.last_entry_date),
                    },
                    width: columnWidths[3] || 'flex-1',
                  }
                : {
                    value: {
                      type: 'string',
                      text: t('table_no_entries'),
                    },
                    width: columnWidths[3] || 'flex-1',
                  },
              {
                value: {
                  type: 'string',
                  text:
                    project.type === 'project'
                      ? sharedT('Filter.UserType.client')
                      : sharedT('Filter.UserType.contractor'),
                },
                width: columnWidths[4] || 'flex-1',
              },
              project.unread_notification_count > 0
                ? {
                    value: {
                      type: 'number',
                      number: project.unread_notification_count,
                    },
                    isRightMost: true,
                    width: columnWidths[5] || 'flex-1',
                  }
                : {
                    value: {
                      type: 'string',
                      text: t('table_no_entries'),
                    },
                    isRightMost: true,
                    width: columnWidths[5] || 'flex-1',
                  },
            ]}
            type={'single'}
            isFirstRow={index === 0}
            isLastRow={index === projects.length - 1}
          />
        ))}
      </TableBody>
    </CustomTable>
  );
};
